/**
 * LandingPage — a vitrine do Orçamigo, em `/`.
 *
 * ─── QUEM VÊ ESTA TELA ──────────────────────────────────────────────────────
 *
 * Só quem chega pela WEB e ainda não tem sessão. As outras duas situações
 * continuam como antes, e as duas por um motivo próprio:
 *
 * · No app INSTALADO (`Capacitor.isNativePlatform()`) a rota `/` segue sendo o
 *   `OnboardingPage`. Uma vitrine dentro do aplicativo seria uma propaganda
 *   para quem já comprou — e o carrossel de lá termina em "Entrar ou criar
 *   conta" e "Testar sem criar conta", que é o que essa pessoa precisa.
 *   É por PLATAFORMA, e não por domínio: `app.orcamigo.com.br` responde 302
 *   para o site (a hospedagem serve um único domínio), então o hostname não
 *   distingue nada.
 * · Logado, `/` manda para `/dashboard`, exatamente como o onboarding já fazia.
 *
 * ─── O QUE ESTA PÁGINA PODE AFIRMAR ─────────────────────────────────────────
 *
 * Só o que o produto faz de fato. Não há número de usuários, depoimento,
 * avaliação nem preço nesta tela: preço vive no RevenueCat e mudá-lo lá
 * deixaria a vitrine mentindo sem ninguém perceber. O plano PRO é apresentado
 * pelo que ele libera e pelos 7 dias de teste; o valor aparece na assinatura,
 * onde é sempre o verdadeiro.
 *
 * O caminho de VISITANTE ("Testar sem criar conta") é preservado de propósito:
 * ele existe hoje no onboarding e é o que deixa a pessoa montar um orçamento
 * inteiro antes de decidir (`/quotes/new` funciona sem sessão).
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  FileText,
  FileSignature,
  ClipboardList,
  ReceiptText,
  Receipt,
  Wallet,
  CalendarDays,
  Package,
  Users,
  BookOpen,
  ShoppingCart,
  WifiOff,
  Check,
  ArrowRight,
} from 'lucide-react';
import { Button3D } from '@/components/ui/button-3d';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const LOGO =
  'https://kizuizwbhlttcmgmllst.supabase.co/storage/v1/object/public/brand-assets/logo-orcamigo.png';

/** O ciclo comercial que o app fecha, na ordem em que ele acontece. */
const CICLO = [
  { icon: FileText, titulo: 'Orçamento', texto: 'Monte a proposta com itens do seu catálogo e envie por WhatsApp.' },
  { icon: FileSignature, titulo: 'Contrato', texto: 'O cliente assina pelo celular, por um link — sem imprimir nada.' },
  { icon: ClipboardList, titulo: 'Ordem de serviço', texto: 'Registre equipamento, diagnóstico e a assinatura na entrega.' },
  { icon: ReceiptText, titulo: 'Recibo', texto: 'Comprovante de pagamento com valor por extenso, pronto para enviar.' },
  { icon: Receipt, titulo: 'Nota fiscal', texto: 'Emissão de NFS-e para quem tem CNPJ, direto do aplicativo.' },
] as const;

/** O que sustenta o dia a dia em volta dos documentos. */
const MODULOS = [
  { icon: Wallet, titulo: 'Financeiro', texto: 'O que entrou, o que falta receber e o que está vencido.' },
  { icon: CalendarDays, titulo: 'Agenda', texto: 'Compromissos e vencimentos no mesmo calendário.' },
  { icon: Users, titulo: 'Clientes', texto: 'Histórico completo de cada pessoa, com tudo o que já foi feito.' },
  { icon: BookOpen, titulo: 'Catálogo', texto: 'Serviços e produtos com preço, custo e unidade.' },
  { icon: Package, titulo: 'Estoque', texto: 'Baixa automática quando o orçamento é aprovado.' },
  { icon: ShoppingCart, titulo: 'Venda rápida', texto: 'Balcão para vender sem montar orçamento.' },
] as const;

const NO_PLANO_GRATIS = [
  'Orçamentos, ordens de serviço e recibos',
  'Clientes, catálogo e estoque',
  'Financeiro, agenda e venda rápida',
  'PDF pronto para enviar por WhatsApp',
] as const;

const NO_PLANO_PRO = [
  'Orçamentos sem limite mensal',
  'Contratos com assinatura digital',
  'Modelos de PDF e cor da sua marca',
  'Relatórios avançados e exportação',
  'Fotos nos itens e impressão em bobina',
] as const;

const CARTAO = 'rounded-2xl border border-border/60 bg-card p-5 shadow-sm';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Mesmo comportamento do onboarding: quem já tem sessão não vê a vitrine.
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <img src={LOGO} alt="Orçamigo" className="h-auto w-32 sm:w-40" width={1536} height={585} />
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/auth')}
              className="rounded-full px-3 py-2 text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
            >
              Entrar
            </button>
            <Button3D size="sm" onClick={() => navigate('/auth')}>
              Criar conta grátis
            </Button3D>
          </div>
        </div>
      </header>

      <main>
        {/* ─── Chamada ───────────────────────────────────────────────────── */}
        <section className="bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 py-14 text-white sm:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold leading-tight sm:text-5xl">
              Do orçamento ao recibo, no mesmo lugar
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base text-white/85 sm:text-lg">
              O Orçamigo é para quem presta serviço e vende: monte a proposta em minutos, mande pelo
              WhatsApp e acompanhe o que foi aprovado, executado e pago.
            </p>
            <div className="mx-auto mt-8 flex max-w-sm flex-col gap-3 sm:max-w-md sm:flex-row sm:justify-center">
              <Button3D color="light" className="sm:flex-1" onClick={() => navigate('/auth')}>
                Criar conta grátis
              </Button3D>
              {/* O visitante monta um orçamento inteiro sem cadastro — é o
                  caminho que o onboarding do app já oferece. */}
              <Button3D color="gray" className="sm:flex-1" onClick={() => navigate('/quotes/new')}>
                Testar sem criar conta
              </Button3D>
            </div>
            <p className="mt-4 text-sm text-white/70">Em português, para MEI, autônomos e pequenas empresas.</p>
          </div>
        </section>

        {/* ─── O ciclo ───────────────────────────────────────────────────── */}
        <section className="px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">Um serviço, do começo ao fim</h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
              Cada documento nasce do anterior — o contrato puxa os dados do orçamento, a ordem de
              serviço puxa do contrato, e o recibo fecha a conta.
            </p>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {CICLO.map(({ icon: Icon, titulo, texto }, i) => (
                <div key={titulo} className={cn(CARTAO, 'flex flex-col')}>
                  <div className="mb-3 flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-xs font-semibold text-muted-foreground">Etapa {i + 1}</span>
                  </div>
                  <h3 className="font-semibold">{titulo}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{texto}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Funciona sem internet ─────────────────────────────────────── */}
        <section className="bg-muted/40 px-4 py-14 sm:py-20">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <WifiOff className="h-8 w-8" />
            </span>
            <div>
              <h2 className="text-2xl font-bold sm:text-3xl">Funciona sem internet</h2>
              <p className="mt-2 text-muted-foreground">
                No meio de uma obra, num sítio ou num porão sem sinal, o app continua funcionando:
                você cadastra o cliente, monta o orçamento e tira as fotos. Quando a conexão volta,
                tudo sobe sozinho.
              </p>
            </div>
          </div>
        </section>

        {/* ─── Os módulos ────────────────────────────────────────────────── */}
        <section className="px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">E o resto do negócio junto</h2>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {MODULOS.map(({ icon: Icon, titulo, texto }) => (
                <div key={titulo} className={CARTAO}>
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold">{titulo}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{texto}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Planos ────────────────────────────────────────────────────── */}
        <section className="bg-muted/40 px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">Comece de graça</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
              O plano grátis já fecha o ciclo do serviço. O PRO tira os limites e libera o que
              depende de marca e assinatura.
            </p>

            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={cn(CARTAO, 'flex flex-col')}>
                <h3 className="text-lg font-bold">Grátis</h3>
                <p className="mt-1 text-sm text-muted-foreground">Para começar hoje, sem cartão.</p>
                <ul className="mt-5 space-y-2.5">
                  {NO_PLANO_GRATIS.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-2">
                  <Button3D className="w-full" onClick={() => navigate('/auth')}>
                    Criar conta grátis
                  </Button3D>
                </div>
              </div>

              <div className={cn(CARTAO, 'flex flex-col border-primary/40 ring-1 ring-primary/20')}>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold">PRO</h3>
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    7 dias grátis
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">Tudo do grátis, mais:</p>
                <ul className="mt-5 space-y-2.5">
                  {NO_PLANO_PRO.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {/* Sem preço nesta tela de propósito: ele vive na loja/assinatura,
                    e um número escrito aqui viraria mentira na primeira mudança. */}
                <div className="mt-6 pt-2">
                  <Button3D color="amber" className="w-full" onClick={() => navigate('/auth')}>
                    Testar o PRO por 7 dias
                  </Button3D>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Chamada final ─────────────────────────────────────────────── */}
        <section className="px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">Seu próximo orçamento sai em minutos</h2>
            <div className="mx-auto mt-7 max-w-xs">
              <Button3D className="w-full" onClick={() => navigate('/auth')}>
                Criar conta grátis
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button3D>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/60 px-4 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 text-sm text-muted-foreground sm:flex-row">
          <span>© Orçamigo</span>
          <nav className="flex items-center gap-5">
            <button onClick={() => navigate('/terms')} className="hover:text-foreground">
              Termos de uso
            </button>
            <button onClick={() => navigate('/privacy')} className="hover:text-foreground">
              Privacidade
            </button>
            <a href="mailto:contato@orcamigo.com.br" className="hover:text-foreground">
              contato@orcamigo.com.br
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/**
 * A rota `/`: vitrine na web, onboarding no app instalado.
 *
 * A escolha é feita aqui, e não dentro de cada página, para que exista UM lugar
 * respondendo "o que é a home" — e para o guard de teste ter o que medir.
 */
export function isLandingPlatform(): boolean {
  return !Capacitor.isNativePlatform();
}
